import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, PawPrint, User, Stethoscope, Syringe } from 'lucide-react';
import { getVaccinationStatus } from '../../utils/vaccinationStatus';
import { Pet, Owner, Vaccination, Examination } from '../../types';

interface PetDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  pet: (Pet & { owner?: Owner; vaccinations?: Vaccination[]; examinations?: Examination[] }) | null;
}

const PetDetailModal: React.FC<PetDetailModalProps> = ({ isOpen, onClose, pet }) => {
  if (!pet) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] flex flex-col relative border border-gray-200 mx-2 sm:mx-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
                <div className="flex items-center space-x-4">
                    <div className="p-3 bg-blue-100 rounded-full">
                        <PawPrint className="text-blue-600" size={24}/>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">{pet.name}</h2>
                        <p className="text-gray-500 capitalize">{pet.species} / {pet.breed}</p>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                    <X className="text-gray-500"/>
                </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto">
                {/* Pet & Owner Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="card-sm">
                        <h3 className="card-header-sm flex items-center"><PawPrint size={16} className="mr-2"/>Hasta Bilgileri</h3>
                        <div className="p-4 space-y-2 text-sm">
                            <p><strong className="text-gray-600">Protokol No:</strong> {pet.protocolNo || 'N/A'}</p>
                           <p><strong className="text-gray-600">Yaş:</strong> {(() => { const m = Number(pet.age||0); if(m<12) return `${m} aylık`; const y=Math.floor(m/12); const r=m%12; return r===0?`${y} yaşında`:`${y} yaş ${r} aylık`; })()}</p>
                            <p><strong className="text-gray-600">Cinsiyet:</strong> <span className="capitalize">{pet.gender}</span></p>
                        </div>
                    </div>
                    <div className="card-sm">
                        <h3 className="card-header-sm flex items-center"><User size={16} className="mr-2"/>Sahip Bilgileri</h3>
                        <div className="p-4 space-y-2 text-sm">
                            <p><strong className="text-gray-600">Adı:</strong> {pet.owner?.name || 'Bilinmiyor'}</p>
                            <p><strong className="text-gray-600">Telefon:</strong> {pet.owner?.phone || 'Bilinmiyor'}</p>
                        </div>
                    </div>
                </div>

                {/* Medical History Tabs */}
                <div className="space-y-6">
                    {/* Examinations */}
                     <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center"><Stethoscope className="mr-2 text-purple-500"/>Muayene Geçmişi</h3>
                        <div className="space-y-3">
                            {pet.examinations && pet.examinations.length > 0 ? pet.examinations.map(exam => (
                                <div key={exam.id} className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm">
                                    <p className="font-semibold text-gray-800">Tarih: {formatDate(exam.date)}</p>
                                    <p className="mt-1"><strong className="text-gray-600">Teşhis:</strong> {exam.diagnosis}</p>
                                    <p className="mt-1"><strong className="text-gray-600">Tedavi:</strong> {exam.treatment}</p>
                                </div>
                            )) : <p className="text-sm text-gray-500 italic">Muayene kaydı bulunamadı.</p>}
                        </div>
                    </div>

                    {/* Vaccinations */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center"><Syringe className="mr-2 text-green-500"/>Aşı Geçmişi</h3>
                        <div className="space-y-3">
                            {pet.vaccinations && pet.vaccinations.length > 0 ? pet.vaccinations.map(vacc => (
                                <div key={vacc.id} className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <p className="font-semibold text-gray-800">{vacc.name}</p>
                                            {(() => {
                                                const st = getVaccinationStatus(vacc, pet.vaccinations || []);
                                                const map = {
                                                    completed: ['Tamamlandı', 'bg-green-100 text-green-800'],
                                                    overdue: ['Gecikmiş', 'bg-red-100 text-red-800'],
                                                    approaching: ['Yaklaşıyor', 'bg-amber-100 text-amber-800'],
                                                    upcoming: ['Planlandı', 'bg-blue-100 text-blue-800'],
                                                } as const;
                                                return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${map[st][1]}`}>{map[st][0]}</span>;
                                            })()}
                                        </div>
                                        <p className="text-gray-600">{formatDate(vacc.date)}</p>
                                    </div>
                                    {vacc.next_due_date && <p className="text-xs text-blue-600 mt-1">Sonraki Doz: {formatDate(vacc.next_due_date)}</p>}
                                </div>
                            )) : <p className="text-sm text-gray-500 italic">Aşı kaydı bulunamadı.</p>}
                        </div>
                    </div>
                </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PetDetailModal;
